import { t } from "@lingui/macro";
import { functions } from "@repo/ui";
import { httpsCallable } from "firebase/functions";
import { FlaskConical, Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { CollapsibleSettingsPanel } from "./CollapsibleSettingsPanel";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

type Configuration =
	| { available: false }
	| {
			available: true;
			mode: "api" | "mock-http";
			operations: string[];
			idRequiredOperations?: string[];
	  };
type TestResult = {
	mode: "api" | "mock-http";
	status: string;
	answer?: string;
	result?: boolean;
	transferToHuman?: boolean;
	message?: string;
	providerResponse?: unknown;
};

// One deliberate submission per click: no automatic regional replay after an
// ambiguous timeout, which could duplicate external provider requests.
const getTools = (companyId: string) =>
	httpsCallable<{ companyId: string }, Configuration>(
		functions,
		"getSancarTestTools",
	)({ companyId });
const runTool = httpsCallable<
	{
		companyId: string;
		operation: string;
		input: { license_number: string; id_number?: string };
	},
	TestResult
>(functions, "testSancarTool", { timeout: 60_000 });

export function SancarToolsPanel({ companyId }: { companyId: string }) {
	const [config, setConfig] = useState<Configuration | null>(null);
	const [loadError, setLoadError] = useState(false);
	const [reload, setReload] = useState(0);
	const [operation, setOperation] = useState("location");
	const [license, setLicense] = useState("");
	const [identity, setIdentity] = useState("");
	const [busy, setBusy] = useState(false);
	const submitting = useRef(false);
	const [result, setResult] = useState<TestResult | null>(null);
	const [error, setError] = useState(false);
	const toolResponse = result
		? Object.fromEntries(
				Object.entries(result).filter(
					([key]) => key !== "mode" && key !== "providerResponse",
				),
			)
		: null;
	// Older backends still require these IDs; new builds return their exact policy.
	const requiresId =
		config?.available === true &&
		(
			config.idRequiredOperations ?? ["release", "debt", "belongings_release"]
		).includes(operation);
	const labels: Record<string, string> = {
		location: t({ id: "sancar.location", message: "Vehicle location" }),
		tender: t({ id: "sancar.tender", message: "Upcoming tender" }),
		release: t({ id: "sancar.release", message: "Vehicle release" }),
		debt: t({ id: "sancar.debt", message: "Vehicle debt" }),
		representative: t({
			id: "sancar.representative",
			message: "Handling office",
		}),
		belongings_release: t({
			id: "sancar.belongings",
			message: "Belongings release",
		}),
	};
	// biome-ignore lint/correctness/useExhaustiveDependencies: reload explicitly retries discovery.
	useEffect(() => {
		let active = true;
		setLoadError(false);
		setConfig(null);
		getTools(companyId)
			.then(({ data }) => {
				if (active) setConfig(data);
			})
			.catch(() => {
				if (active) setLoadError(true);
			});
		return () => {
			active = false;
		};
	}, [companyId, reload]);
	if (config?.available === false) return null;
	return (
		<CollapsibleSettingsPanel
			title={t({ id: "sancar.title", message: "Sancar tool testing" })}
			description={t({
				id: "sancar.description",
				message:
					"Check tool answers without starting an agent call. Each submission runs a fresh lookup.",
			})}
			icon={<FlaskConical className="h-5 w-5" aria-hidden="true" />}
		>
			{loadError ? (
				<div role="alert" className="space-y-3">
					<p>
						{t({
							id: "sancar.loadError",
							message: "Unable to load Sancar tools.",
						})}
					</p>
					<Button
						variant="outline"
						onClick={() => setReload((value) => value + 1)}
					>
						{t({ id: "sancar.retry", message: "Try again" })}
					</Button>
				</div>
			) : !config ? (
				<p role="status">
					{t({ id: "sancar.loading", message: "Loading Sancar tools…" })}
				</p>
			) : (
				<div className="max-w-xl space-y-6">
					<p className="text-sm text-muted-foreground">
						{config.mode === "mock-http"
							? t({
									id: "sancar.mockMode",
									message: "Mock mode — results are test data only.",
								})
							: t({
									id: "sancar.apiMode",
									message:
										"API mode — requests use your company's Sancar connection.",
								})}
					</p>
					<form
						className="space-y-4"
						onSubmit={async (event) => {
							event.preventDefault();
							if (submitting.current) return;
							submitting.current = true;
							setBusy(true);
							setResult(null);
							setError(false);
							try {
								const response = await runTool({
									companyId,
									operation,
									input: {
										license_number: license,
										...(requiresId ? { id_number: identity } : {}),
									},
								});
								setResult(response.data);
							} catch {
								setError(true);
							} finally {
								submitting.current = false;
								setBusy(false);
							}
						}}
					>
						<fieldset disabled={busy} className="space-y-4">
							<div className="space-y-2">
								<Label htmlFor="sancar-tool">
									{t({ id: "sancar.tool", message: "Tool" })}
								</Label>
								<select
									id="sancar-tool"
									value={operation}
									onChange={(event) => {
										setOperation(event.target.value);
										setIdentity("");
										setResult(null);
										setError(false);
									}}
									className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
								>
									{config.operations.map((value) => (
										<option key={value} value={value}>
											{labels[value] ?? value}
										</option>
									))}
								</select>
							</div>
							<div className="grid gap-4 sm:grid-cols-2">
								<div className="space-y-2">
									<Label htmlFor="sancar-license">
										{t({ id: "sancar.license", message: "License number" })}
									</Label>
									<Input
										id="sancar-license"
										dir="ltr"
										inputMode="numeric"
										pattern="[0-9]{1,32}"
										maxLength={32}
										required
										autoComplete="off"
										value={license}
										onChange={(event) => {
											setLicense(event.target.value);
											setResult(null);
											setError(false);
										}}
									/>
								</div>
								{requiresId && (
									<div className="space-y-2">
										<Label htmlFor="sancar-id">
											{t({ id: "sancar.identity", message: "ID number" })}
										</Label>
										<Input
											id="sancar-id"
											dir="ltr"
											inputMode="numeric"
											pattern="[0-9]{1,32}"
											maxLength={32}
											required
											autoComplete="off"
											value={identity}
											onChange={(event) => {
												setIdentity(event.target.value);
												setResult(null);
												setError(false);
											}}
										/>
									</div>
								)}
							</div>
							{operation === "belongings_release" && (
								<p className="text-sm text-muted-foreground">
									{t({
										id: "sancar.unfinished",
										message:
											"This tool's business rules are not implemented yet. Release information is unavailable.",
									})}
								</p>
							)}
							{requiresId &&
								(operation === "debt" || operation === "release") && (
									<p className="text-sm text-muted-foreground">
										{t({
											id: "sancar.temporaryVerification",
											message:
												"Temporary verification accepts nine digits ending in 0.",
										})}
									</p>
								)}
							<Button
								type="submit"
								disabled={
									busy ||
									!/^[0-9]{1,32}$/.test(license) ||
									(requiresId && !/^[0-9]{1,32}$/.test(identity))
								}
							>
								{busy && (
									<Loader2
										className="me-2 h-4 w-4 animate-spin"
										aria-hidden="true"
									/>
								)}
								{busy
									? t({ id: "sancar.running", message: "Checking…" })
									: t({ id: "sancar.submit", message: "Run tool" })}
							</Button>
						</fieldset>
					</form>
					{error && (
						<p role="alert" className="text-sm text-destructive">
							{t({
								id: "sancar.runError",
								message:
									"The request could not be completed. It may have reached Sancar. Submit again to run a new lookup.",
							})}
						</p>
					)}
					{result && (
						<section
							aria-live="polite"
							aria-atomic="true"
							className="space-y-2 border-t pt-4"
						>
							<h4 className="font-medium">
								{t({ id: "sancar.answer", message: "Tool answer" })}
							</h4>
							{typeof result.transferToHuman === "boolean" && (
								<dl className="grid grid-cols-2 gap-2 text-sm">
									<dt>
										{t({
											id: "sancar.humanTransfer",
											message: "Suggest human transfer",
										})}
									</dt>
									<dd>
										{result.transferToHuman
											? t({ id: "sancar.yes", message: "Yes" })
											: t({ id: "sancar.no", message: "No" })}
									</dd>
								</dl>
							)}
							{typeof result.result === "boolean" && (
								<p className="text-sm">
									{t({ id: "sancar.boolean", message: "Result" })}:{" "}
									<code>{String(result.result)}</code>
								</p>
							)}
							<p dir="auto" className="whitespace-pre-wrap break-words">
								{result.message ??
									result.answer ??
									(result.status === "verification_unavailable"
										? t({
												id: "sancar.verificationUnavailable",
												message:
													"Identity verification is unavailable. No protected vehicle information was disclosed.",
											})
										: result.status === "id_mismatch"
											? t({
													id: "sancar.idMismatch",
													message:
														"The ID does not match the mock record. No protected vehicle information was disclosed.",
												})
											: t({
													id: "sancar.failed",
													message:
														"The vehicle check could not be completed. No vehicle status is available.",
												}))}
							</p>
							<details className="min-w-0 pt-2">
								<summary className="cursor-pointer rounded-sm text-sm font-medium outline-none focus-visible:ring-2 focus-visible:ring-ring">
									{t({
										id: "sancar.advancedJson",
										message: "Advanced: JSON responses",
									})}
								</summary>
								<h5 className="mt-4 text-sm font-medium">
									{t({
										id: "sancar.agentJson",
										message: "Agent tool response JSON",
									})}
								</h5>
								<pre
									dir="ltr"
									tabIndex={0}
									aria-label={t({
										id: "sancar.agentJson",
										message: "Agent tool response JSON",
									})}
									className="mt-3 max-h-96 max-w-full overflow-auto rounded-md border border-border bg-muted p-3 text-start text-xs leading-5 outline-none focus-visible:ring-2 focus-visible:ring-ring"
								>
									<code>{JSON.stringify(toolResponse, null, 2)}</code>
								</pre>
								<h5 className="mt-4 text-sm font-medium">
									{t({
										id: "sancar.providerJson",
										message: "Provider JSON response",
									})}
								</h5>
								{result.providerResponse !== undefined ? (
									<pre
										dir="ltr"
										tabIndex={0}
										aria-label={t({
											id: "sancar.providerJson",
											message: "Provider JSON response",
										})}
										className="mt-3 max-h-96 max-w-full overflow-auto rounded-md border border-border bg-muted p-3 text-start text-xs leading-5 outline-none focus-visible:ring-2 focus-visible:ring-ring"
									>
										<code>
											{JSON.stringify(result.providerResponse, null, 2)}
										</code>
									</pre>
								) : (
									<p className="mt-3 text-sm text-muted-foreground">
										{t({
											id: "sancar.noJson",
											message:
												"No JSON response is available for this request.",
										})}
									</p>
								)}
							</details>
						</section>
					)}
				</div>
			)}
		</CollapsibleSettingsPanel>
	);
}
